import { useRef } from "react";
import ElectricalSafetyReport from "pages/files/image/report/electricalSafety/electricalSafetyReport";
import Ud1000SummaryTable from "pages/files/image/report/ud1000/components/Ud1000SummaryTable";
import Ud1000VideoSummaryTable from "pages/files/video/report/components/Ud1000VideoSummaryTable";
import ProjectReportUd1000PdfCoverIndexPage from "./ProjectReportUd1000PdfCoverIndexPage.jsx";
import ProjectReportUd1000PdfCoverIntroPage from "./ProjectReportUd1000PdfCoverIntroPage.jsx";
import ProjectReportUd1000PdfCoverOverviewPage from "./ProjectReportUd1000PdfCoverOverviewPage.jsx";
import {
  getFusionResultTone,
  getImageFusionLabelsByLocale,
  getVideoFusionLabelsByLocale,
  normalizeFusionResultCode,
} from "lib/utils/fusionResult";
import {
  buildElectricalPreviewImages,
  buildUd1000PreviewMediaItems,
  getSafeImageReportDraftData,
  mergeUd1000PreviewImages,
} from "./utils";

export default function ProjectReportPreviewPage({
  page,
  t,
  compact = false,
  forExport = false,
  selectedReportCategory = "",
  i18n,
  currentUser = null,
  onRequireElectricalTripleSpot = null,
  previewMaxHeight = null,
}) {
  const rootClassName = `projectReportPreviewPage${compact ? " is-compact" : ""}${
    forExport ? " is-export" : ""
  }${page?.isEmpty ? " is-emptyPage" : ""}`;
  const ultrasonicRef = useRef(null);
  const thermalRef = useRef(null);
  const fftRef = useRef(null);
  const uvRef = useRef(null);
  const videoUltrasonicRef1 = useRef(null);
  const videoUltrasonicRef2 = useRef(null);
  const videoFftRef1 = useRef(null);
  const videoFftRef2 = useRef(null);
  const isUd1000Selected = selectedReportCategory === "ud1000_fusion";
  const isElectricalDutySelected = selectedReportCategory === "electrical_duty";
  const isUd1000Page =
    isUd1000Selected &&
    !page?.isEmpty &&
    ["image", "video"].includes(page?.file?.file_type);
  const isUd1000TemplateCover =
    !page?.isEmpty && page?.isUd1000TemplateCover;
  const isElectricalDutyPage =
    isElectricalDutySelected &&
    !page?.isEmpty &&
    page?.file?.file_type === "image";
  const previewImages = isUd1000Page
    ? mergeUd1000PreviewImages(page.file)
    : null;
  const imageFusionLabels = getImageFusionLabelsByLocale(i18n);
  const videoFusionLabels = getVideoFusionLabelsByLocale(i18n);
  const imageFusionCode = normalizeFusionResultCode(
    page?.file?.fusion_result_code ?? page?.file?.fusion_result,
    0,
  );
  const ud1000FusionTone = isUd1000Page
    ? getFusionResultTone(page?.file?.fusion_result_code ?? page?.file?.fusion_result)
    : null;
  const ud1000FusionToneClass =
    ud1000FusionTone === "warning"
      ? "active1"
      : ud1000FusionTone === "abnormal"
        ? "active2"
        : ud1000FusionTone === "recheck"
          ? "active3"
          : "";
  const displayFusionLabel = isUd1000Page
    ? page?.file?.file_type === "image"
      ? (imageFusionLabels?.[imageFusionCode] ??
        page?.file?.fusion_result ??
        "")
      : (videoFusionLabels?.[
          normalizeFusionResultCode(
            page?.file?.fusion_result_code ?? page?.file?.fusion_result,
            0,
          )
        ] ??
        page?.file?.fusion_result ??
        "")
    : "";
  const mediaItems = isUd1000Page
    ? buildUd1000PreviewMediaItems(page.file, previewImages, t)
    : page.mediaItems || [];
  const ud1000PreviewMaxHeight =
    previewMaxHeight == null
      ? null
      : Math.max(previewMaxHeight - (compact ? 20 : 32), 0);

  if (isUd1000TemplateCover) {
    if (page.coverPageType === "intro") {
      return (
        <ProjectReportUd1000PdfCoverIntroPage
          projectName={page.projectName}
          periodEndYearMonth={page.periodEndYearMonth}
          imageSection={page.imageSection}
          headline={page.headline}
          contractor={page.contractor}
          logoSrc={page.logoSrc}
        />
      );
    }

    if (page.coverPageType === "overview") {
      return (
        <ProjectReportUd1000PdfCoverOverviewPage
          contractName={page.contractName}
          periodStart={page.periodStart}
          periodEnd={page.periodEnd}
          contractor={page.contractor}
          participantText={page.participantText}
          participantCount={page.participantCount}
          participantNames={page.participantNames}
          purpose={page.purpose}
          analysisResultText={page.analysisResultText}
          diagnosisResult={page.diagnosisResult}
          analysisOpinionLines={page.analysisOpinionLines}
          logoSrc={page.logoSrc}
        />
      );
    }

    return (
      <ProjectReportUd1000PdfCoverIndexPage
        rows={page.rows}
        showCaption={page.showIndexCaption !== false}
        logoSrc={page.logoSrc}
      />
    );
  }

  if (isUd1000Page) {
    if (page.file.file_type === "image") {
      return (
        <div
          className={`${rootClassName} is-ud1000Report reportPanel ${
            ud1000FusionToneClass
          } ${
            compact ? "has-overlayTitle" : ""
          }`}
        >
          {compact && (
            <div className="projectReportPreviewPageTitle">{page.title}</div>
          )}
          <div className="reportPanelContent">
            <Ud1000SummaryTable
              draftData={getSafeImageReportDraftData(page.file)}
              file={page.file}
              t={t}
              disableResponsiveScale={forExport}
              maxViewportHeight={ud1000PreviewMaxHeight}
              imgTemp={previewImages}
              displayFusionLabel={displayFusionLabel}
              ultrasonicRef={ultrasonicRef}
              thermalRef={thermalRef}
              fftRef={fftRef}
              uvRef={uvRef}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className={`${rootClassName} is-ud1000Report reportPanel ${
          ud1000FusionToneClass
        } ${
          compact ? "has-overlayTitle" : ""
        }`}
      >
        {compact && (
          <div className="projectReportPreviewPageTitle">{page.title}</div>
        )}
        <div className="reportPanelContent">
          <Ud1000VideoSummaryTable
            t={t}
            tableRef={null}
            draftData={page.file}
            disableResponsiveScale={forExport}
            maxViewportHeight={ud1000PreviewMaxHeight}
            imgTemp={previewImages}
            toFusionLabel={() => displayFusionLabel}
            ultrasonicRef1={videoUltrasonicRef1}
            ultrasonicRef2={videoUltrasonicRef2}
            fftRef1={videoFftRef1}
            fftRef2={videoFftRef2}
          />
        </div>
      </div>
    );
  }

  if (isElectricalDutyPage) {
    const electricalPreviewUser = {
      ...(currentUser || {}),
      ...(page?.file || {}),
    };
    const electricalPreviewMaxHeight =
      previewMaxHeight == null
        ? null
        : Math.max(previewMaxHeight - (compact ? 20 : 32), 0);

    return (
      <div
        className={`${rootClassName} is-electricalDutyReport reportPanel ${
          compact ? "has-overlayTitle" : ""
        }${page?.requiresElectricalAnalysis ? " is-analysisPending" : ""}`}
      >
        {compact && (
          <div className="projectReportPreviewPageTitle">{page.title}</div>
        )}
        <ElectricalSafetyReport
          file={page.file}
          images={buildElectricalPreviewImages(page.file)}
          user={electricalPreviewUser}
          onRequireTripleSpot={() => onRequireElectricalTripleSpot?.(page)}
          disableResponsiveScale={forExport}
          maxViewportHeight={electricalPreviewMaxHeight}
        />
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      {!compact ? (
        <div className="projectReportPreviewPageSubtitle">{page.subtitle}</div>
      ) : null}

      {page.meta?.length && !compact ? (
        <div className="projectReportPreviewPageInfoGrid">
          {page.meta.map((item) => (
            <div
              className="projectReportPreviewPageInfo"
              key={`${page.id}-${item.label}`}
            >
              <div className="projectReportPreviewPageInfoLabel">
                {item.label}
              </div>
              <div className="projectReportPreviewPageInfoValue">
                {item.value}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {mediaItems?.length ? (
        <div className="projectReportPreviewPageMediaGrid">
          {mediaItems.map((item) => {
            const mediaStyle = item.src
              ? { backgroundImage: `url(${item.src})` }
              : undefined;

            return (
              <div
                key={`${page.id}-${item.id}`}
                className={`projectReportPreviewPageMedia ${
                  item.src ? "has-image" : "is-empty"
                }`}
                role="img"
                aria-label={`${page.title} ${item.label}`}
                style={mediaStyle}
              >
                <div className="projectReportPreviewPageMediaLabel">
                  {item.label}
                </div>
                {item.src ? null : (
                  <div className="projectReportPreviewPageMediaEmpty">
                    {t("projectDetail.projectReportEditor.noPreviewImage", {
                      defaultValue: "No preview image",
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="projectReportPreviewPageEmptyState">
          {page.title ||
            t("projectDetail.projectReportEditor.noPreviewImage", {
              defaultValue: "No preview image",
            })}
        </div>
      )}
    </div>
  );
}
